/**
 * Settings → General (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md, WS-B).
 *
 * The portal's central settings that belong to no single section: today the
 * email sender identities. Sections with their own settings (Analytics,
 * Tiers, LLM Profiles, …) keep them on their own pages.
 *
 * Endpoints:
 *   GET  /api/admin/settings?category=general
 *   PUT  /api/admin/settings/:key · DELETE /api/admin/settings/:key
 *   POST /api/admin/settings/test-email  { identity }
 *   GET  /api/admin/auth/me   (to show edit controls only to settings.write holders)
 */
import React, { useState } from 'react';
import { Mail, Send, Settings as SettingsIcon } from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { adminFetch, adminMutate } from '../../../utils/adminApi';
import { SettingField } from './SettingField';
import { AdminMe, SettingsResponse, adminCan, errorMessage } from './settingsLogic';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

function useMe() {
  return useQuery({
    queryKey: ['admin', 'me'],
    queryFn: async () => (await adminFetch<{ admin: AdminMe }>('/api/admin/auth/me')).admin,
  });
}

const TestEmailButton: React.FC<{ identity: 'support' | 'notifications' }> = ({ identity }) => {
  const [state, setState] = useState<{ busy: boolean; message: string | null; ok: boolean }>({
    busy: false, message: null, ok: false,
  });
  const send = async () => {
    setState({ busy: true, message: null, ok: false });
    try {
      const res = await adminMutate<{ from: string; to: string }>(
        '/api/admin/settings/test-email', 'POST', { identity },
      );
      setState({ busy: false, ok: true, message: `Sent from ${res.from} to ${res.to}.` });
    } catch (err) {
      setState({ busy: false, ok: false, message: errorMessage(err) });
    }
  };
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={send}
        disabled={state.busy}
        className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        <Send className="h-3 w-3" /> {state.busy ? 'Sending…' : 'Send me a test'}
      </button>
      {state.message && (
        <span className={`text-xs ${state.ok ? 'text-green-700' : 'text-red-600'}`} role="status">
          {state.message}
        </span>
      )}
    </div>
  );
};

const GeneralInner: React.FC = () => {
  const me = useMe();
  const settings = useQuery({
    queryKey: ['admin', 'settings', 'general'],
    queryFn: () => adminFetch<SettingsResponse>('/api/admin/settings?category=general'),
  });
  const canEdit = adminCan(me.data, 'settings.write');
  const supportKeys = new Set(['email.support_from_address', 'email.support_from_name']);
  const notificationKeys = new Set(['email.notifications_from_address', 'email.notifications_from_name', 'email.notifications_reply_to']);
  const all = settings.data?.settings ?? [];
  const support = all.filter(s => supportKeys.has(s.key));
  const notifications = all.filter(s => notificationKeys.has(s.key));
  const other = all.filter(s => !supportKeys.has(s.key) && !notificationKeys.has(s.key));
  const info = settings.data?.info ?? {};

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
        <SettingsIcon className="h-5 w-5" style={{ color: '#1F6C4F' }} /> General
      </h1>
      <p className="mt-1 text-sm text-gray-500">
        Settings that belong to the whole portal. Every change is recorded in the audit log.
      </p>

      {settings.isLoading && <div className="mt-6 text-sm text-gray-500">Loading…</div>}
      {settings.error && (
        <div className="mt-6 rounded-md bg-red-50 p-3 text-sm text-red-700">{errorMessage(settings.error)}</div>
      )}

      {settings.data && (
        <>
          <section className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              <Mail className="h-4 w-4" /> Email senders
            </h2>

            <div className="flex flex-col gap-2 border-b border-gray-100 py-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="sm:max-w-md">
                <div className="text-sm font-semibold text-gray-900">System email (to users)</div>
                <div className="mt-0.5 text-xs text-gray-500">
                  The From on every email users receive. Changed deliberately in {info.system_from_source ?? 'AWS Secrets Manager'}, not here.
                </div>
              </div>
              <span className="text-sm text-gray-700">{info.system_from_address ?? 'Unavailable'}</span>
            </div>

            {notifications.map(s => (
              <SettingField key={s.key} setting={s} canEdit={canEdit} onChanged={() => settings.refetch()} />
            ))}
            {notifications.length > 0 && canEdit && (
              <div className="flex justify-end pb-2 pt-3">
                <TestEmailButton identity="notifications" />
              </div>
            )}

            {support.map(s => (
              <SettingField key={s.key} setting={s} canEdit={canEdit} onChanged={() => settings.refetch()} />
            ))}
            {support.length > 0 && canEdit && (
              <div className="flex justify-end pt-3">
                <TestEmailButton identity="support" />
              </div>
            )}
            <p className="mt-3 text-[11px] text-gray-400">
              Sender addresses must be on @{info.authenticated_domain ?? 'coachcedar.com'}, the only domain SendGrid is authenticated for.
            </p>
          </section>

          {other.length > 0 && (
            <section className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
              {other.map(s => (
                <SettingField key={s.key} setting={s} canEdit={canEdit} onChanged={() => settings.refetch()} />
              ))}
            </section>
          )}

          {!canEdit && me.data && (
            <p className="mt-4 text-xs text-gray-500">You can view these settings. Changing them needs the settings.write capability.</p>
          )}
        </>
      )}
    </div>
  );
};

const SettingsGeneral: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <GeneralInner />
  </QueryClientProvider>
);

export default SettingsGeneral;
